import { useState } from 'react';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { nestedLocationsApi, sortOrderParams } from '../axios/nestedLocations';
import { useLanguage } from '../hooks';
import { useNestedLocationStore } from '../newStore/nestedLocationStore';
import { NestedLocationPayload, NestedLocationResponse } from '../types/nestedLocations';
import { SortByNameOrder } from '../utils/sortByName';

const NESTED_LOCATIONS_INDEX_QUERY_KEY = 'nestedLocationsIndex';
const NESTED_LOCATION_QUERY_KEY = 'nestedLocation';
export const ACCOUNT_STATUS_WITH_LOCATIONS_QUERY_KEY = 'accountStatusWithLocations';

interface UseGetNestedLocationsParams {
  searchValue: string;
  currentPage: number;
  itemsPerPage: number;
  // Overrides the default `only_root: !searchValue`. Pass `false` to list the
  // full tree (all levels) even with an empty search, e.g. the "@" mention
  // listing that shows every location, not just root sites.
  onlyRoot?: boolean;
  /** False keeps the request from firing at all. Used by a caller that gates the data
   * by permission, where filtering the response after the fact still leaks the fetch. */
  enabled?: boolean;
  /** Absent by default, so every caller that does not sort keeps requesting unsorted. */
  order?: SortByNameOrder;
}

const initialLocationsResponse: NestedLocationResponse = {
  locations: [],
  next_page_exists: false,
  prev_page_exists: false,
  total_locations: 0,
};

export function useGetNestedLocations({
  searchValue,
  currentPage,
  itemsPerPage,
  onlyRoot,
  // Optional and defaulted, so every existing caller behaves exactly as before. The
  // AskDataScope "@" panel needs it: it gates the Lugares category by permission and
  // was filtering the RESULT while still firing the request, which is the same
  // mismatch its Usuarios and Grupos gates exist to avoid.
  enabled = true,
  order = 'unsorted',
}: UseGetNestedLocationsParams) {
  const resolvedOnlyRoot = onlyRoot ?? !searchValue;
  return useQuery({
    enabled,
    // The order belongs in the key: without it two different orders share one cache
    // entry, and cycling back to a recently used order serves the other one's rows.
    queryKey: [NESTED_LOCATIONS_INDEX_QUERY_KEY, searchValue, currentPage, resolvedOnlyRoot, order],
    queryFn: () =>
      nestedLocationsApi.getNestedLocations({
        search: searchValue,
        only_root: resolvedOnlyRoot,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        ...sortOrderParams(order),
      }),
    placeholderData: initialLocationsResponse,
  });
}

export function useGetAccountStatusWithLocations(enabled = true) {
  return useQuery({
    queryKey: [ACCOUNT_STATUS_WITH_LOCATIONS_QUERY_KEY],
    queryFn: () => nestedLocationsApi.checkAccountStatusWithLocations(),
    enabled,
  });
}

export function useGetNestedLocation(id: number | undefined) {
  return useQuery({
    queryKey: [NESTED_LOCATION_QUERY_KEY, id],
    queryFn: () => {
      if (!id) throw new Error('ID is required');
      return nestedLocationsApi.getNestedLocationById(id);
    },
    enabled: !!id,
  });
}

// DS-4043/DS-4062: the Usuarios block. `enabled` lets the caller hold off the
// request until the block scrolls into view (useLazyBlock, AC-16).
export function useGetBranchUsers(locationId: number, search: string, enabled: boolean) {
  return useQuery({
    queryKey: ['nestedLocationBranchUsers', locationId, search],
    queryFn: () => nestedLocationsApi.getBranchUsers(locationId, search || undefined),
    enabled: enabled && !!locationId,
    // Without this, `data` (and so `total_count`) briefly goes undefined on
    // every keystroke-triggered refetch -- UsersBlock's search box is only
    // rendered while `total_count > 0`, so it was unmounting and remounting
    // on each one, dropping focus and whatever was typed in that instant.
    placeholderData: keepPreviousData,
  });
}

/* Hook to invalidate nested locations queries after some mutations */
const useInvalidateNestedLocationsQueries = (invalidateIndexQuery = true) => {
  const queryClient = useQueryClient();
  const loadAllLocations = useNestedLocationStore((state) => state.actions.loadAllLocations);

  const invalidateQueries = (locationId?: number) => {
    // Note: loadAllLocations will update the local state of the nested locations used in nested location selector.
    loadAllLocations();
    queryClient.invalidateQueries({
      queryKey: [ACCOUNT_STATUS_WITH_LOCATIONS_QUERY_KEY],
      exact: false,
    });

    if (locationId) {
      queryClient.invalidateQueries({ queryKey: [NESTED_LOCATION_QUERY_KEY, locationId] });
    }

    // Note: invalidateIndexQuery can be false in some cases like deleting a location, since the delete process is manual.
    if (invalidateIndexQuery) {
      queryClient.invalidateQueries({ queryKey: [NESTED_LOCATIONS_INDEX_QUERY_KEY], exact: false });
    }
  };

  return { invalidateQueries };
};

export function useDeleteLocationMutation() {
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  // Note: We should not invalidate locationsIndex query here because it will cause a re-fetch of all locations and the expanded locations will be lost.
  const { invalidateQueries } = useInvalidateNestedLocationsQueries(false);

  return useMutation({
    mutationFn: (locationId: number) => {
      return nestedLocationsApi.deleteNestedLocations(locationId);
    },
    onSuccess: (_data, locationId) => {
      invalidateQueries(locationId);
      enqueueSnackbar(lang.locations.deleteConfirmation.success, { variant: 'success' });
    },
    onError: (error) => {
      console.error('Error deleting location:', error);
      const message = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      const errorMessage = message ? `: ${message}` : '';
      enqueueSnackbar(lang.locations.deleteConfirmation.error + errorMessage, { variant: 'error' });
    },
  });
}

export function useCreateLocationMutation() {
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const { invalidateQueries } = useInvalidateNestedLocationsQueries();

  return useMutation({
    mutationFn: (data: NestedLocationPayload) => {
      return nestedLocationsApi.createNestedLocation(data);
    },
    onSuccess: () => {
      invalidateQueries();
      enqueueSnackbar(lang.locations.create.success, { variant: 'success' });
    },
    onError: (error) => {
      const message = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      const errorMessage = message ? `: ${message}` : '';
      console.error('Error creating location:', error, message);
      enqueueSnackbar(lang.locations.create.error + errorMessage, { variant: 'error' });
    },
  });
}

export function useUpdateLocationMutation() {
  const lang = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const { invalidateQueries } = useInvalidateNestedLocationsQueries();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<NestedLocationPayload> }) => {
      return nestedLocationsApi.updateNestedLocation(id, data);
    },
    onSuccess: (_data, variables) => {
      invalidateQueries(variables.id);
      enqueueSnackbar(lang.locations.update.success, { variant: 'success' });
    },
    onError: (error) => {
      console.error('Error updating location:', error);
      const message = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
      const errorMessage = message ? `: ${message}` : '';
      enqueueSnackbar(lang.locations.update.error + errorMessage, { variant: 'error' });
    },
  });
}

export function useImportNestedLocationsMutation() {
  const { enqueueSnackbar } = useSnackbar();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { invalidateQueries } = useInvalidateNestedLocationsQueries();
  const lang = useLanguage();

  const mutation = useMutation({
    mutationFn: (file: File) => {
      return nestedLocationsApi.importNestedLocations(file);
    },
    onSuccess: (data) => {
      invalidateQueries();
      enqueueSnackbar(data.message, { variant: 'success' });
      setErrorMessage(null);
    },
    onError: (error: AxiosError<{ message?: string; error?: string }>) => {
      let errorMsg = lang.locations.import.errors.importError;

      if (error && typeof error === 'object' && 'response' in error) {
        const response = error.response;
        errorMsg = response?.data?.message || response?.data?.error || errorMsg;
      }
      setErrorMessage(errorMsg);
    },
  });

  return {
    ...mutation,
    errorMessage,
  };
}
